import { useState, useEffect } from 'react';
import { 
  Users, Phone, TrendingUp, Calendar, RefreshCw, BarChart3, Activity,
  PhoneCall, PhoneIncoming, PhoneOutgoing, Clock, MessageSquare, Zap,
  Award, Target, CheckCircle, Eye
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { listExtensions, getCallLogs } from '../../services/ringcentralService';

// Cache key and duration
const CACHE_KEY = 'recruiter_performance';
const CACHE_DURATION_MINUTES = 5;

interface RecruiterStats {
  recruiter_email: string;
  recruiter_name: string;
  recruiter_phone: string;
  calls_today: number;
  calls_this_week: number;
  calls_this_month: number;
  total_calls_all_time: number;
  inbound_calls?: number;
  outbound_calls?: number;
  total_duration?: number;
  avg_duration?: number;
}

interface RecentCall {
  id: string;
  recruiter_name: string;
  recruiter_phone: string;
  direction: string;
  from_phone: string;
  to_phone: string;
  start_time: string;
  duration: number;
  result: string;
}

export function RecruiterPerformanceView() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RecruiterStats[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [allCallsData, setAllCallsData] = useState<any[]>([]);

  useEffect(() => {
    loadPerformanceData();
  }, []);

  // Auto-refresh Live Activity every 60 seconds
  useEffect(() => {
    const refreshLiveActivity = async () => {
      if (stats.length > 0) {
        try {
          // Fetch fresh call data from RingCentral for live activity
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const callsResponse = await getCallLogs({
            dateFrom: thirtyDaysAgo.toISOString(),
            direction: 'Outbound',
            perPage: 100, // Just get recent 100 calls for live feed
            page: 1
          });
          
          const calls = callsResponse.records || [];
          
          // Build phone mapping
          const { data: users } = await supabase
            .from('profiles')
            .select('email, full_name, phone')
            .eq('role', 'Recruiting');
          
          const phoneMapping: Record<string, { name: string; email: string; phone: string }> = {};
          (users || []).forEach(user => {
            if (user.phone) {
              const normalizedPhone = user.phone.trim().replace(/\D/g, '');
              phoneMapping[normalizedPhone] = {
                name: user.full_name,
                email: user.email,
                phone: user.phone.trim()
              };
            }
          });
          
          // Filter and map calls
          const recentCallsData: RecentCall[] = calls
            .filter(call => {
              const fromPhone = call.from?.phoneNumber?.replace(/\D/g, '') || '';
              const normalizedFromPhone = fromPhone.startsWith('1') ? fromPhone.substring(1) : fromPhone;
              return phoneMapping[normalizedFromPhone] !== undefined;
            })
            .slice(0, 50)
            .map(call => {
              const fromPhone = call.from?.phoneNumber?.replace(/\D/g, '') || '';
              const normalizedFromPhone = fromPhone.startsWith('1') ? fromPhone.substring(1) : fromPhone;
              const recruiterInfo = phoneMapping[normalizedFromPhone];
              const recruiterData = stats.find(s => s.recruiter_email === recruiterInfo?.email);
              
              return {
                id: call.id,
                recruiter_name: recruiterInfo?.name || 'Unknown',
                recruiter_phone: recruiterInfo?.phone || 'N/A',
                direction: call.direction,
                from_phone: call.from?.phoneNumber || call.from?.extensionNumber || 'Unknown',
                to_phone: call.to?.phoneNumber || 'Unknown',
                start_time: call.startTime,
                duration: call.duration || 0,
                result: call.result || 'Unknown'
              };
            });
          
          setRecentCalls(recentCallsData);
        } catch (error) {
          console.error('Error refreshing live activity:', error);
        }
      }
    };

    // Initial load after stats are available
    if (stats.length > 0 && recentCalls.length === 0) {
      refreshLiveActivity();
    }

    // Set up interval to refresh every 60 seconds
    const interval = setInterval(refreshLiveActivity, 60000);

    return () => clearInterval(interval);
  }, [stats, recentCalls.length]);

  const loadPerformanceData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // Check Supabase cache first (unless force refresh)
      if (!forceRefresh) {
        const { data: cachedData, error: cacheError } = await supabase
          .from('ringcentral_performance_cache')
          .select('data, created_at, expires_at')
          .eq('cache_key', CACHE_KEY)
          .single();
        
        if (!cacheError && cachedData) {
          const now = new Date();
          const expiresAt = new Date(cachedData.expires_at);
          
          if (expiresAt > now) {
            const age = Math.round((now.getTime() - new Date(cachedData.created_at).getTime()) / 1000);
            console.log(`📦 Using cached data from Supabase (${age}s old)`);
            setStats(cachedData.data);
            setLastUpdated(new Date(cachedData.created_at));
            
            // Build phoneToUser mapping for Live Activity even when using cache
            const { data: users } = await supabase
              .from('profiles')
              .select('email, full_name, phone')
              .eq('role', 'Recruiting');
            
            const phoneMapping: Record<string, { name: string; email: string; phone: string }> = {};
            (users || []).forEach(user => {
              if (user.phone) {
                const normalizedPhone = user.phone.trim().replace(/\D/g, '');
                phoneMapping[normalizedPhone] = {
                  name: user.full_name,
                  email: user.email,
                  phone: user.phone.trim()
                };
              }
            });
            
            // Load recent activity with proper mapping
            await loadRecentActivityWithMapping(cachedData.data, phoneMapping);
            setLoading(false);
            return;
          } else {
            console.log('🔄 Cache expired, fetching fresh data...');
          }
        }
      }
      
      // Get extension mapping from RingCentral
      let extensionMap: Record<string, string> = {};
      let extensionToUser: Record<string, { name: string; email: string }> = {};
      
      try {
        const extensionsData = await listExtensions();
        extensionsData.records.forEach((ext: any) => {
          if (ext.extensionNumber && ext.id) {
            extensionMap[ext.extensionNumber] = ext.id;
          }
        });
        console.log('📞 Extension mapping loaded:', Object.keys(extensionMap).length, 'extensions');
      } catch (error) {
        console.error('Error fetching extensions:', error);
        showToast('error', 'Failed to fetch RingCentral extensions');
      }

      // Fetch all calls from RingCentral API
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Get first day of current month for filtering
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      
      // Fetch last 30 days from API to match RingCentral search
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      let allCalls: any[] = [];
      try {
        console.log('📊 Fetching calls from RingCentral API (last 30 days)');
        
        let page = 1;
        let hasMore = true;
        
        while (hasMore && page <= 15) {
          const callsResponse = await getCallLogs({
            dateFrom: thirtyDaysAgo.toISOString(),
            direction: 'Outbound',
            perPage: 1000,
            page: page
          });
          
          const records = callsResponse.records || [];
          allCalls = allCalls.concat(records);
          
          hasMore = callsResponse.navigation?.nextPage !== undefined;
          console.log(`📄 Page ${page}: ${records.length} calls (Total: ${allCalls.length})`);
          
          if (hasMore && page < 15) {
            page++;
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            break;
          }
        }
        
        console.log(`✅ Fetched ${allCalls.length} total calls from RingCentral`);
        setAllCallsData(allCalls);
      } catch (error) {
        console.error('Error fetching calls:', error);
        showToast('error', 'Failed to fetch call data from RingCentral');
      }

      // Get all recruiters
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('email, full_name, phone, ringcentral_extension_id')
        .eq('role', 'Recruiting');

      if (usersError) throw usersError;

      const recruiterStats: RecruiterStats[] = [];

      // Build phone number to user mapping
      const phoneToUser: Record<string, { name: string; email: string; phone: string }> = {};
      for (const user of users || []) {
        if (user.phone) {
          // Normalize phone number (trim whitespace and remove non-digits)
          const normalizedPhone = user.phone.trim().replace(/\D/g, '');
          phoneToUser[normalizedPhone] = {
            name: user.full_name,
            email: user.email,
            phone: user.phone.trim()
          };
        }
      }
      
      console.log('📱 Phone to User mapping:', phoneToUser);

      for (const user of users || []) {
        let callsToday = 0;
        let callsWeek = 0;
        let callsMonth = 0;
        let inboundCalls = 0;
        let outboundCalls = 0;
        let totalDuration = 0;

        const extensionNumber = user.ringcentral_extension_id;
        const actualExtensionId = extensionMap[extensionNumber];
        const userPhone = user.phone ? user.phone.trim().replace(/\D/g, '') : '';
        
        console.log(`👤 ${user.full_name} - Phone: "${user.phone}", Normalized: ${userPhone}, Extension: ${extensionNumber}`);
        
        // Match calls by from phone number (since all calls are from main recruiting line)
        const extensionCalls = allCalls.filter(call => {
          const fromPhone = call.from?.phoneNumber?.replace(/\D/g, '') || '';
          // Remove leading 1 if present (country code)
          const normalizedFromPhone = fromPhone.startsWith('1') ? fromPhone.substring(1) : fromPhone;
          return normalizedFromPhone === userPhone;
        });
        
        console.log(`  📞 Found ${extensionCalls.length} calls from phone ${user.phone}`);
        
        // Debug Dorian's calls specifically
        if (user.full_name === 'Dorian' && extensionCalls.length > 0) {
          console.log('  🔍 Dorian\'s call dates:');
          extensionCalls.forEach((call, idx) => {
            console.log(`    ${idx + 1}. ${new Date(call.startTime).toLocaleString()} - from: ${call.from?.phoneNumber}`);
          });
        }
        
        // Store mapping for Live Activity by phone number
        if (extensionCalls.length > 0) {
          // Use the extension ID from these calls for mapping
          const callExtId = extensionCalls[0].extension?.id;
          if (callExtId) {
            extensionToUser[callExtId] = {
              name: user.full_name,
              email: user.email
            };
          }
        }

        if (extensionCalls.length > 0) {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          callsToday = extensionCalls.filter(call => new Date(call.startTime) >= today).length;
          callsWeek = extensionCalls.filter(call => new Date(call.startTime) >= weekAgo).length;
          callsMonth = extensionCalls.filter(call => new Date(call.startTime) >= thirtyDaysAgo).length;
          
          // Calculate additional metrics
          extensionCalls.forEach(call => {
            if (call.direction === 'Inbound') inboundCalls++;
            if (call.direction === 'Outbound') outboundCalls++;
            if (call.duration) totalDuration += call.duration;
          });

          console.log(`👤 ${user.full_name} - Today: ${callsToday}, Week: ${callsWeek}, Month: ${callsMonth}`);
        }

        const totalCallsAllTime = extensionCalls.length;
        const avgDuration = totalCallsAllTime > 0 ? Math.round(totalDuration / totalCallsAllTime) : 0;

        recruiterStats.push({
          recruiter_email: user.email,
          recruiter_name: user.full_name,
          recruiter_phone: user.phone || 'N/A',
          calls_today: callsToday,
          calls_this_week: callsWeek,
          calls_this_month: callsMonth,
          total_calls_all_time: totalCallsAllTime,
          inbound_calls: inboundCalls,
          outbound_calls: outboundCalls,
          total_duration: totalDuration,
          avg_duration: avgDuration
        });
      }

      // Set stats first so it's available for recent calls mapping
      setStats(recruiterStats);
      
      // Debug: Log extension mapping and actual extension numbers in calls
      console.log('🔍 Extension to User mapping:', extensionToUser);
      console.log('🔍 Sample call structure:', allCalls[0]);
      
      // Log unique extension numbers found in calls
      const uniqueExtensions = new Set(allCalls.map(call => call.extension?.extensionNumber).filter(Boolean));
      console.log('🔍 Unique extension numbers in calls:', Array.from(uniqueExtensions));
      
      // Log unique from phone numbers in first 10 calls
      console.log('🔍 Sample from phone numbers:');
      allCalls.slice(0, 10).forEach((call, i) => {
        console.log(`  Call ${i}: from=${call.from?.phoneNumber}, name=${call.from?.name}, direction=${call.direction}`);
      });
      
      // Extract recent calls for activity feed - only from known recruiters
      const recentCallsData: RecentCall[] = allCalls
        .filter(call => {
          // Only include calls from known recruiter phone numbers
          const fromPhone = call.from?.phoneNumber?.replace(/\D/g, '') || '';
          const normalizedFromPhone = fromPhone.startsWith('1') ? fromPhone.substring(1) : fromPhone;
          return phoneToUser[normalizedFromPhone] !== undefined;
        })
        .slice(0, 50)
        .map((call, index) => {
          const fromPhone = call.from?.phoneNumber?.replace(/\D/g, '') || '';
          const normalizedFromPhone = fromPhone.startsWith('1') ? fromPhone.substring(1) : fromPhone;
          const recruiterInfo = phoneToUser[normalizedFromPhone];
          const recruiterData = recruiterStats.find(s => s.recruiter_email === recruiterInfo?.email);
          
          // Debug first few calls
          if (index < 3) {
            console.log(`🔍 Call ${index}:`, {
              fromPhone: call.from?.phoneNumber,
              normalizedFromPhone,
              recruiterInfo,
              recruiterData: recruiterData ? { name: recruiterData.recruiter_name, phone: recruiterData.recruiter_phone } : null,
              to: call.to
            });
          }
          
          return {
            id: call.id,
            recruiter_name: recruiterInfo?.name || 'Unknown',
            recruiter_phone: recruiterInfo?.phone || 'N/A',
            direction: call.direction,
            from_phone: call.from?.phoneNumber || call.from?.extensionNumber || 'Unknown',
            to_phone: call.to?.phoneNumber || 'Unknown',
            start_time: call.startTime,
            duration: call.duration || 0,
            result: call.result || 'Unknown'
          };
        });
      
      setRecentCalls(recentCallsData);
      const now = new Date();
      setLastUpdated(now);
      
      // Save to Supabase cache
      const expiresAt = new Date(now.getTime() + CACHE_DURATION_MINUTES * 60 * 1000);
      
      await supabase
        .from('ringcentral_performance_cache')
        .upsert({
          cache_key: CACHE_KEY,
          data: recruiterStats,
          created_at: now.toISOString(),
          expires_at: expiresAt.toISOString()
        }, {
          onConflict: 'cache_key'
        });
      
      console.log('💾 Data cached successfully in Supabase');
    } catch (error) {
      console.error('Error loading performance data:', error);
      showToast('error', 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentActivityWithMapping = async (statsData: RecruiterStats[], phoneMapping: Record<string, { name: string; email: string; phone: string }>) => {
    try {
      const { data: calls } = await supabase
        .from('recruiter_calls')
        .select('id, recruiter_email, direction, candidate_phone, start_time, duration, call_result')
        .order('start_time', { ascending: false })
        .limit(50);
      
      if (calls) {
        const activities: RecentCall[] = calls
          .filter(call => {
            // Only include calls from known recruiters
            const recruiterData = statsData.find(s => s.recruiter_email === call.recruiter_email);
            return recruiterData !== undefined;
          })
          .map(call => {
            const recruiterData = statsData.find(s => s.recruiter_email === call.recruiter_email);
            return {
              id: call.id,
              recruiter_name: recruiterData?.recruiter_name || 'Unknown',
              recruiter_phone: recruiterData?.recruiter_phone || 'N/A',
              direction: call.direction,
              from_phone: recruiterData?.recruiter_phone || 'Unknown',
              to_phone: call.candidate_phone,
              start_time: call.start_time,
              duration: call.duration || 0,
              result: call.call_result || 'Unknown'
            };
          });
        setRecentCalls(activities);
      }
    } catch (error) {
      console.error('Error loading recent activity:', error);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const { data: calls } = await supabase
        .from('recruiter_calls')
        .select('id, recruiter_email, direction, candidate_phone, start_time, duration, call_result')
        .order('start_time', { ascending: false })
        .limit(50);
      
      if (calls) {
        const activities: RecentCall[] = calls.map(call => {
          const recruiterData = stats.find(s => s.recruiter_email === call.recruiter_email);
          return {
            id: call.id,
            recruiter_name: recruiterData?.recruiter_name || 'Unknown',
            recruiter_phone: recruiterData?.recruiter_phone || 'N/A',
            direction: call.direction,
            from_phone: recruiterData?.recruiter_phone || 'Unknown',
            to_phone: call.candidate_phone,
            start_time: call.start_time,
            duration: call.duration || 0,
            result: call.call_result || 'Unknown'
          };
        });
        setRecentCalls(activities);
      }
    } catch (error) {
      console.error('Error loading recent activity:', error);
    }
  };

  const totalCallsToday = stats.reduce((sum, s) => sum + s.calls_today, 0);
  const totalCallsWeek = stats.reduce((sum, s) => sum + s.calls_this_week, 0);
  const totalCallsMonth = stats.reduce((sum, s) => sum + s.calls_this_month, 0);
  const avgCallsPerRecruiter = stats.length > 0 ? Math.round(totalCallsMonth / stats.length) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-4 rounded-xl shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Recruiter Performance Dashboard
                </h1>
                <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  Real-time analytics powered by RingCentral API (cached for 5 minutes)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-semibold text-slate-900">
                    {stats.length} Recruiters
                  </span>
                </div>
              </div>
              <button
                onClick={() => loadPerformanceData(true)}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:shadow-md transition-all font-medium text-slate-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-2">
            Last updated: {lastUpdated.toLocaleString()}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">Loading performance data from RingCentral...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Overview Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-md">
                    <PhoneCall className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">Today</span>
                </div>
                <p className="text-4xl font-bold text-slate-900 mb-1">{totalCallsToday}</p>
                <p className="text-sm text-slate-500 font-medium">Total Calls Today</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl shadow-md">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">Last 7 Days</span>
                </div>
                <p className="text-4xl font-bold text-slate-900 mb-1">{totalCallsWeek}</p>
                <p className="text-sm text-slate-500 font-medium">Last 7 Days</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-md">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">30 Days</span>
                </div>
                <p className="text-4xl font-bold text-slate-900 mb-1">{totalCallsMonth}</p>
                <p className="text-sm text-slate-500 font-medium">Last 30 Days</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl shadow-md">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">Avg</span>
                </div>
                <p className="text-4xl font-bold text-slate-900 mb-1">{avgCallsPerRecruiter}</p>
                <p className="text-sm text-slate-500 font-medium">Avg Per Recruiter</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Performance Table */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Individual Performance
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase">Recruiter</th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-slate-700 uppercase">Today</th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-slate-700 uppercase">Last 7 Days</th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-slate-700 uppercase">Last 30 Days</th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-slate-700 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {stats.map((recruiter) => (
                          <tr key={recruiter.recruiter_email} className="hover:bg-blue-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">
                                  {recruiter.recruiter_name.charAt(0)}
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-900">{recruiter.recruiter_name}</div>
                                  <div className="text-xs text-slate-500 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {recruiter.recruiter_phone}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`text-2xl font-bold ${
                                recruiter.calls_today >= 40 ? 'text-green-600' : 
                                recruiter.calls_today >= 20 ? 'text-orange-600' : 'text-red-600'
                              }`}>
                                {recruiter.calls_today}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="text-lg font-semibold text-slate-700">{recruiter.calls_this_week}</span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="text-lg font-semibold text-blue-600">{recruiter.calls_this_month}</span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="text-xl font-bold text-indigo-600">{recruiter.total_calls_all_time}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Live Activity Feed */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden h-full">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Activity className="w-5 h-5" />
                        Live Activity
                      </h2>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-sm text-white font-medium">Live</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 max-h-[400px] overflow-y-auto">
                    {recentCalls.length === 0 ? (
                      <div className="text-center py-12">
                        <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm">No recent activity</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {recentCalls.map((call) => {
                          const timeAgo = new Date(call.start_time);
                          const now = new Date();
                          const diffMs = now.getTime() - timeAgo.getTime();
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMins / 60);
                          
                          let timeDisplay = '';
                          if (diffHours > 24) timeDisplay = `${Math.floor(diffHours / 24)}d ago`;
                          else if (diffHours > 0) timeDisplay = `${diffHours}h ago`;
                          else if (diffMins > 0) timeDisplay = `${diffMins}m ago`;
                          else timeDisplay = 'Just now';

                          return (
                            <div key={call.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all">
                              <div className={`p-2 rounded-lg ${
                                call.direction === 'Inbound' ? 'bg-green-100' : 'bg-blue-100'
                              }`}>
                                {call.direction === 'Inbound' ? (
                                  <PhoneIncoming className="w-4 h-4 text-green-600" />
                                ) : (
                                  <PhoneOutgoing className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="font-semibold text-sm text-slate-900 truncate">{call.recruiter_name}</p>
                                  <span className="text-xs text-slate-500 font-medium whitespace-nowrap ml-2">{timeDisplay}</span>
                                </div>
                                <p className="text-xs text-slate-500 mb-0.5 truncate">
                                  From: {call.from_phone}
                                </p>
                                <p className="text-xs text-slate-600 mb-1 truncate">
                                  {call.direction} → {call.to_phone}
                                </p>
                                {call.duration > 0 && (
                                  <span className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200 font-medium text-slate-700 inline-flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {Math.floor(call.duration / 60)}m {call.duration % 60}s
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Performers */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <Award className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Most Calls Today</h3>
                </div>
                {[...stats].sort((a, b) => b.calls_today - a.calls_today).slice(0, 5).map((r, i) => (
                  <div key={r.recruiter_email} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-400 w-6">#{i + 1}</span>
                      <span className="font-semibold text-slate-900 text-sm">{r.recruiter_name}</span>
                    </div>
                    <span className="text-lg font-bold text-slate-900">{r.calls_today}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Most Calls This Week</h3>
                </div>
                {[...stats].sort((a, b) => b.calls_this_week - a.calls_this_week).slice(0, 5).map((r, i) => (
                  <div key={r.recruiter_email} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-400 w-6">#{i + 1}</span>
                      <span className="font-semibold text-slate-900 text-sm">{r.recruiter_name}</span>
                    </div>
                    <span className="text-lg font-bold text-slate-900">{r.calls_this_week}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Most Calls This Month</h3>
                </div>
                {[...stats].sort((a, b) => b.calls_this_month - a.calls_this_month).slice(0, 5).map((r, i) => (
                  <div key={r.recruiter_email} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-400 w-6">#{i + 1}</span>
                      <span className="font-semibold text-slate-900 text-sm">{r.recruiter_name}</span>
                    </div>
                    <span className="text-lg font-bold text-slate-900">{r.calls_this_month}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
